/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */
import { useEffect, useRef } from "react";
import { Edge, EdgeChange } from "@xyflow/react";
import { Button } from "baseui/button";
import React from "react";
import { useDexterTheme } from "../../DesignSystem/Theme/ThemeContext";
import { ProjectAction } from "../../ProjectState/ProjectActions";

interface EdgeContextMenuProps {
  x: number;
  y: number;
  edge: Edge;
  onClose: () => void;
  projectDispatch: React.Dispatch<ProjectAction>;
}

export function EdgeContextMenu({
  x,
  y,
  edge,
  onClose,
  projectDispatch,
}: EdgeContextMenuProps): JSX.Element {
  const { currentTheme } = useDexterTheme();
  const menuRef = useRef<HTMLDivElement | null>(null);

  const handleDelete = (): void => {
    const change: EdgeChange = {
      id: edge.id,
      type: "remove",
    };
    projectDispatch(["changeEdges", { changes: [change] }, projectDispatch]);
    onClose();
  };

  // Explicit containment-based outside-click detection, same pattern used
  // for the input node's overflow menu click away. A blanket "close on any click"
  // listener races against the Delete button's own onClick with no
  // guaranteed order, which is what was swallowing the click before it
  // reached handleDelete. Checking .contains() means we only ever close
  // for a click that's genuinely outside the user menu.
  useEffect(() => {
    const handlePointerDown = (event: PointerEvent): void => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    document.addEventListener("pointerdown", handlePointerDown, true);
    return () =>
      document.removeEventListener("pointerdown", handlePointerDown, true);
  }, [onClose]);

  return (
    <div
      ref={menuRef}
      data-context-menu-boundary
      style={{
        position: "fixed",
        top: y,
        left: x,
        zIndex: 1000,
        borderRadius: "6px",
        border: `1px solid ${currentTheme.colors.controlBarBorderColor}`,
        minWidth: "160px",
      }}
    >
      <div
        style={{
          padding: "4px",
          backgroundColor: currentTheme.colors.backgroundTertiary,
        }}
      >
        {/* Button style taken from InputSearchPage Button */}
        <Button
          onClick={handleDelete}
          overrides={{
            BaseButton: {
              style: {
                height: "45px",
                paddingLeft: currentTheme.spacing.md,
                paddingRight: currentTheme.spacing.md,
                backgroundColor: "transparent",
                border: "none",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: currentTheme.spacing.xs,
                fontFamily: currentTheme.fonts.primary,
                fontSize: currentTheme.fontSizes.medium,
                color: currentTheme.colors.negative,
                whiteSpace: "nowrap",
                ":hover": {
                  backgroundColor: currentTheme.colors.primaryButtonColor,
                  color: currentTheme.colors.primaryElementFontColor,
                },
              },
            },
          }}
        >
          Delete Edge
        </Button>
      </div>
    </div>
  );
}
